import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Insights as InsightsIcon,
  AutoAwesome as AutoAwesomeIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';

interface USP {
  icon: React.ReactNode;
  stat: string;
  title: string;
  description: string;
  gradient: string;
}

const USPs: React.FC = () => {
  const theme = useTheme();

  const usps: USP[] = [
    {
      icon: <SpeedIcon sx={{ fontSize: 48 }} />,
      stat: '40%',
      title: 'Faster Sprint Planning',
      description:
        'AI-powered estimation and automated capacity planning cut planning time in half',
      gradient: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    },
    {
      icon: <InsightsIcon sx={{ fontSize: 48 }} />,
      stat: 'Real-Time',
      title: 'Leadership Insights',
      description:
        'Live dashboards with risk assessment and KPIs provide instant visibility into team health',
      gradient: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
    },
    {
      icon: <AutoAwesomeIcon sx={{ fontSize: 48 }} />,
      stat: 'AI-Powered',
      title: 'Intelligent Automation',
      description:
        'Machine learning algorithms learn from your team\'s history to provide accurate estimates',
      gradient: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 48 }} />,
      stat: '< 5 min',
      title: 'Zero Learning Curve',
      description:
        'Intuitive interface with familiar workflows means your team is productive from day one',
      gradient: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 48 }} />,
      stat: '360°',
      title: 'Data-Driven Decisions',
      description:
        'Advanced analytics and team productivity metrics illuminate every aspect of delivery',
      gradient: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
    },
    {
      icon: <SwapHorizIcon sx={{ fontSize: 48 }} />,
      stat: '1-Click',
      title: 'Seamless Migration',
      description:
        'Import from Jira and MS Planner with zero data loss. Your history comes with you',
      gradient: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, 
          ${theme.palette.background.default} 0%, 
          ${alpha(theme.palette.primary.main, 0.02)} 50%,
          ${theme.palette.background.default} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decorations */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '-5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, transparent 70%)`,
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '-5%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(
            theme.palette.secondary.main,
            0.1
          )} 0%, transparent 70%)`,
          filter: 'blur(80px)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ mb: 8 }}>
          {/* Section Badge */}
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.secondary.main,
              fontWeight: 700,
              letterSpacing: 2,
              fontSize: '0.875rem',
            }}
          >
            WHY CHOOSE US
          </Typography>

          {/* Section Title */}
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              maxWidth: '800px',
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Built for Modern Agile Teams
          </Typography>

          {/* Section Subtitle */}
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '700px',
              fontWeight: 400,
              lineHeight: 1.7,
            }}
          >
            Experience the difference with cutting-edge features designed to accelerate your
            delivery and empower your teams
          </Typography>
        </Stack>

        {/* USPs Grid */}
        <Grid container spacing={4}>
          {usps.map((usp, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: 4,
                  background: theme.palette.background.paper,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: 4,
                  transition: 'all 0.4s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: usp.gradient,
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                    '&::before': {
                      transform: 'scaleX(1)',
                    },
                    '& .usp-icon': {
                      transform: 'scale(1.1) rotate(-5deg)',
                    },
                    '& .usp-stat': {
                      transform: 'scale(1.05)',
                    },
                  },
                }}
              >
                <Stack spacing={3} alignItems="center" textAlign="center">
                  {/* Icon */}
                  <Box
                    className="usp-icon"
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: usp.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      transition: 'all 0.4s ease',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    {usp.icon}
                  </Box>

                  {/* Stat */}
                  <Typography
                    className="usp-stat"
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      fontSize: '2.5rem',
                      background: usp.gradient,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      transition: 'all 0.4s ease',
                    }}
                  >
                    {usp.stat}
                  </Typography>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                      fontSize: '1.25rem',
                    }}
                  >
                    {usp.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.8,
                    }}
                  >
                    {usp.description}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Statistics Bar */}
        <Box
          sx={{
            mt: 10,
            p: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Grid container spacing={4} alignItems="center" textAlign="center">
            <Grid item xs={6} sm={3}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  color: theme.palette.primary.main,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                1,000+
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Active Teams
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  color: theme.palette.success.main,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                50,000+
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Projects Delivered
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  color: theme.palette.secondary.main,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                99.9%
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Uptime SLA
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  color: theme.palette.info.main,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Support Available
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default USPs;

